import { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import ContentCreatorStep from "../ContentCreatorStep";
import BiographyField from "../BiographyField";
import LicenseUploaded from "../LicenseUploaded";
import InsuranceUploaded from "../InsuranceUploaded";
import DegreesField from "../DegreesField";
import CertificationsField from "../CertificationsField";
import ManageSocialAccountsFields from "../ManageSocialAccountsFields";
import StepHeader from "../StepHeader";
import SelectableBoxCommon from "../SelectableBoxCommon";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { ConfirmModal } from "components";
import Confirmation from "../Confirmation";
import { useAuth } from "context/authContext";
import { UserService } from "services/UserService";
import { useNavigate } from "react-router-dom";
import { Timestamp } from "firebase/firestore";

type SelectedValueType =
  | "contentCreator"
  | "biography"
  | "degrees"
  | "certifications"
  | "license"
  | "insurance"
  | "manageSocialAccounts"
  | "confirmation";

const BecomeContentCreator = () => {
  const { currentUser, setCurrentUser, updatePendingStatus } = useAuth();
  const navigate = useNavigate();
  const [selectedValue, setSelectedValue] =
    useState<SelectedValueType>("contentCreator");
  const [currentStep, setCurrentStep] = useState(0);

  const [showIcons, setShowIcons] = useState({
    contentCreator: false,
    emailPassword: false,
    biography: false,
    degrees: false,
    certifications: false,
    license: false,
    insurance: false,
    manageSocialAccounts: false,
  });
  const [isCompleted, setIsCompleted] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [biographyData, setBiographyData] = useState({
    biography: "",
  });
  const [degreesData, setDegreesData] = useState({
    degrees: "",
  });
  const [certificationsData, setCertificationsData] = useState({
    certifications: "",
  });
  const [licenseData, setLicenseData] = useState({
    typeOfLicense: "",
    licenseExpiration: Timestamp.now(),
    licenseNumber: "",
    licenseImageUrl: "",
    stateofLicensure: "",
  });
  const [insuranceData, setInsuranceData] = useState({
    insuranceNumber: "",
    insuranceExpiration: Timestamp.now(),
    insuranceImageUrl: "",
  });
  const [socialsData, setSocialsData] = useState({
    instagram: "",
    linkedin: "",
    twitter: "",
    facebook: "",
    other: "",
  });
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({});

  const uid = currentUser.uid;

  // Fetch the user's data from your backend or context (this is a mock function)
  const fetchUserData = async () => {
    try {
      const userData = await UserService.getUserByid(currentUser.uid);
      setBiographyData({
        biography: userData.biography || "",
      });
      setDegreesData({
        degrees: userData.degrees || "",
      });
      setCertificationsData({
        certifications: userData.certifications || "",
      });
      setLicenseData({
        typeOfLicense: userData.typeOfLicense || "",
        stateofLicensure: userData.stateofLicensure || "",
        licenseExpiration: userData.licenseExpiration,
        licenseNumber: userData.licenseNumber || "",
        licenseImageUrl: userData.licenseImageUrl || "",
      });
      setInsuranceData({
        insuranceNumber: userData.insuranceNumber || "",
        insuranceExpiration: userData.insuranceExpiration,
        insuranceImageUrl: userData.insuranceImageUrl || "",
      });
      setSocialsData({
        instagram: userData.instagram || "",
        linkedin: userData.linkedin || "",
        twitter: userData.twitter || "",
        facebook: userData.facebook || "",
        other: userData.other || "",
      });
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUserData();
    }
  }, [currentUser]);



  const handleOpenModal = () => {
    setConfirmModalOpen(true);
  };
  const handleCloseModal = () => setConfirmModalOpen(false);

  const allSteps: SelectedValueType[] = [
    "contentCreator",
    "biography",
    "degrees",
    "certifications",
    "license",
    "insurance",
    "manageSocialAccounts",
    "confirmation",
  ];

  // Condition to skip the first two steps
  const skipFirstTwoSteps = true;
  const steps = skipFirstTwoSteps ? allSteps.slice(0) : allSteps;

  const headers: Record<SelectedValueType, string> = {
    contentCreator: "",
    biography: "Biography",
    degrees: "Degrees",
    certifications: "Certifications",
    license: "Add Your License",
    insurance: "Insurance",
    manageSocialAccounts: "Socials",
    confirmation: "Confirmation",
  };

  // const handleSelectionChange = (value: SelectedValueType) => {
  //   // Find the correct index for the selected step
  //   const stepIndex = allSteps.indexOf(value);
  //   if (stepIndex !== -1) {
  //     setCurrentStep(stepIndex);
  //     setSelectedValue(value);
  //   }
  // };
  const handleSelectionChange = (value: SelectedValueType) => {
    const stepIndex = allSteps.indexOf(value);

    // Prevent clicking on previous completed steps
    if (stepIndex !== -1 && stepIndex === currentStep) {
      setCurrentStep(stepIndex);
      setSelectedValue(value);
    }
  };


  const handleNextClick = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setSelectedValue(steps[currentStep + 1]);
    } else {
      setIsCompleted(true);
    }
  };

  const handleSubmit = async () => {
    // setIsCompleted(true);
    const newErrors: Record<string, string> = {};

    if (!biographyData.biography) newErrors.biography = "Biography is required.";
    if (!degreesData.degrees) newErrors.degrees = "Degree is required.";
    if (!certificationsData.certifications) newErrors.certifications = "Certification is required.";
    if (!licenseData.typeOfLicense) newErrors.typeOfLicense = "License type is required.";
    if (!licenseData.licenseNumber) newErrors.licenseNumber = "License number is required.";
    if (!licenseData.licenseExpiration) newErrors.licenseExpiration = "License expiration is required.";
    if (!licenseData.licenseImageUrl) newErrors.licenseImageUrl = "License image is required.";
    if (!insuranceData.insuranceNumber) newErrors.insuranceNumber = "Insurance number is required.";
    if (!insuranceData.insuranceExpiration) newErrors.insuranceExpiration = "Insurance expiration is required.";
    if (!insuranceData.insuranceImageUrl) newErrors.insuranceImageUrl = "Insurance image is required.";
    if (!socialsData.instagram && !socialsData.linkedin && !socialsData.twitter && !socialsData.facebook && !socialsData.other)
      newErrors.socials = "At least one social account is required.";

    // ✅ If errors exist, set the error state and prevent submission
    if (Object.keys(newErrors).length > 0) {
      setErrorMessages(newErrors);
      return;
    }


    try {
      const data = {
        type: "content-creator",
        isLicenseVerified: false,
        isInsuranceVerified: false,
        accountStatus: "pending",
        userStatus: "active",
        ...biographyData,
        ...degreesData,
        ...certificationsData,
        ...licenseData,
        ...insuranceData,
        ...socialsData,
      };

      await UserService.createUser(uid, data);
      setCurrentUser({ ...currentUser, ...data });

      // ✅ Trigger `updatePendingStatus()` to immediately update UI
      updatePendingStatus();

    } catch (error) {
      console.error("Failed to update user:", error);
    } finally {
      console.log("User updated successfully");
      navigate("/");
    }
  };

  const handleButtonClick = () => {
    // setSelectedValue("contentCreator");
    setShowIcons((prev) => ({
      ...prev,
      contentCreator: true, // ✅ Mark as completed
    }));
    setSelectedValue("biography");
  };

  const handleConfirm = () => {
    // Your confirm logic here
    setConfirmModalOpen(false);
  };

  return (
    <>
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        minHeight="100vh"
        mb={2}
        position={"relative"}
      >
        <Box
          width={{ xs: "100%", md: "22%" }}
          p={2}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <Box>
            <Typography
              variant="h6"
              gutterBottom
              mt={2}
              fontSize={18}
              fontWeight={700}
              color={"#595959"}
            >
              Content Creator
            </Typography>
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="contentCreator"
              // onClick={handleSelectionChange}
              onClick={() => currentStep === allSteps.indexOf("contentCreator") && handleSelectionChange("contentCreator")} // ✅ Prevent clicks on completed steps

              text="Content Creator"
              showIcon={showIcons.contentCreator}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="biography"
              onClick={handleSelectionChange}
              text="Biography"
              showIcon={showIcons.biography}

            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="degrees"
              onClick={handleSelectionChange}
              text="Degrees"
              showIcon={showIcons.degrees}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="certifications"
              onClick={handleSelectionChange}
              text="Certifications"
              showIcon={showIcons.certifications}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="license"
              onClick={handleSelectionChange}
              text="License"
              showIcon={showIcons.license}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="insurance"
              onClick={handleSelectionChange}
              text="Insurance"
              showIcon={showIcons.insurance}
            />
            <SelectableBoxCommon
              selectedValue={selectedValue}
              value="manageSocialAccounts"
              onClick={handleSelectionChange}
              text="Manage Social Accounts"
              showIcon={showIcons.manageSocialAccounts}
            />
            {/* // display errro message /// */}
            <Box pt={6}>
              {Object.values(errorMessages).length > 0 && (
                <Typography sx={{ color: "#E10A0A", fontSize: "15px", fontWeight: 700 }}>
                  Please fill all data of content creator in all tabs before submitting

                </Typography>
              )}
            </Box>
            <Box m={2} pt={2}>
              <Button sx={styleSheet.saveChangesBtnMobile} type="submit">
                Save Changes
              </Button>
            </Box>
          </Box>

          <Box
            display={{ xs: "flex", md: "flex" }}
            flexDirection="column"
            mb={2}
            position={"relative"}
          >
            <Box display="flex" flexDirection="column" gap={3} mt={3}>
              {/* <Typography sx={{ borderBottom: "2px solid #A9A9A9", width: "198px" }}></Typography>
                            <Typography display={"flex"} flexDirection={"column"} alignItems={"flex-start"} mt={1}>
                                <Button sx={styleSheet.textButton}>Privacy Policy</Button>
                                <Button sx={styleSheet.textButton}>Terms of Service</Button>
                            </Typography> */}

              {/* <Button
                onClick={handleButtonClick}
                sx={styleSheet.saveChangesBtn}
                type="submit"
              >
                Save Changes
              </Button> */}
              {/* <Box pt={2} ml={3}>
                <Button
                  onClick={handleOpenModal}
                  sx={styleSheet.buttonDeactive}
                >
                  Deactivate Account
                </Button>
              </Box> */}


            </Box>
          </Box>
        </Box>

        {/* //modal deactive // */}
        <ConfirmModal
          title="You’re About to Deactivate Your Account"
          subtitle="Figma ipsum component variant main layer. Line horizontal arrange team editor thumbnail distribute. Follower figma blur connection slice plugin. Text mask hand bullet ipsum slice mask selection bullet list. Polygon align hand bold."
          cancelText="Cancel"
          confirmText="Confirm"
          titleColor="#E10A0A"
          isLoading={false}
          onCancel={handleCloseModal}
          onConfirm={handleConfirm}
          isConfirmModal={isConfirmModalOpen}
          icon={<ErrorOutlineIcon sx={{ color: "#E10A0A", fontSize: 40 }} />}
        />

        {selectedValue !== "contentCreator" && (
          <Box
            width={{ xs: "100%", md: "80%" }}
            height={{ xs: "1200px", md: "auto" }}
            position={"relative"}
            bgcolor={"#F8F8F8"}
            sx={{ borderRadius: "10px", border: "1px solid #A9A9A9" }}
          >
            <Box sx={{ marginBottom: "60px" }}>
              <StepHeader
                step={currentStep + 1}
                totalSteps={steps.length}
                title={headers[selectedValue]}
              />

              <Typography
                sx={{
                  borderBottom: "2px solid #A9A9A9",
                  width: "100%",
                  mt: 1,
                }}
              ></Typography>
              {
                selectedValue === "biography" ? (
                  <BiographyField
                    biographyData={biographyData}
                    setBiographyData={setBiographyData}
                    setShowIcons={setShowIcons}
                  />
                ) : selectedValue === "degrees" ? (
                  <DegreesField
                    degreesData={degreesData}
                    setDegreesData={setDegreesData}
                    setShowIcons={setShowIcons}
                  />
                ) : selectedValue === "certifications" ? (
                  <CertificationsField
                    certificationsData={certificationsData}
                    setCertificationsData={setCertificationsData}
                    setShowIcons={setShowIcons}
                  />
                ) : selectedValue === "license" ? (
                  <LicenseUploaded
                    licenseData={licenseData}
                    setLicenseData={setLicenseData}
                    onChange={(updatedLicenseData: any) =>
                      console.log(updatedLicenseData)
                    }
                    setShowIcons={setShowIcons}
                  />
                ) : selectedValue === "insurance" ? (
                  <InsuranceUploaded
                    insuranceData={insuranceData}
                    setInsuranceData={setInsuranceData}
                    onChange={(updatedInsuranceData: any) =>
                      console.log(updatedInsuranceData)
                    }
                    setShowIcons={setShowIcons}
                  />
                ) : selectedValue === "manageSocialAccounts" ? (
                  <ManageSocialAccountsFields
                    socialsData={socialsData}
                    setSocialsData={setSocialsData}
                    setShowIcons={setShowIcons}
                  />
                ) : selectedValue === "confirmation" ? (
                  <Confirmation />
                ) : null}
            </Box>
            {selectedValue === "confirmation" ? (
              <Box sx={styleSheet.confrimBtn}>
                <Button sx={styleSheet.conformBtn} onClick={handleSubmit}>
                  Done
                </Button>
              </Box>
            ) : (
              <Box sx={styleSheet.nextBtn7Con}>
                <Button
                  sx={{
                    ...styleSheet.nextBtn,
                    color: showIcons[selectedValue] ? "white" : "#595959",
                    bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                    "&:hover": {
                      bgcolor: showIcons[selectedValue] ? "#233F91" : "#DBDBDB",
                    },
                  }}
                  disabled={!showIcons[selectedValue]}
                  onClick={handleNextClick}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        )}

        {selectedValue === "contentCreator" && (
          <Box width={{ xs: "100%", md: "80%" }}>
            <ContentCreatorStep
              step={currentStep + 1}
              totalSteps={steps.length}
              title="Become a Content Creator"
              titleContent="Step 1: Learn More About Becoming a Content Creator"
              videoUrl="https://www.youtube.com/watch?v=sIspR3rrFkA"
              textContent="Figma ipsum component variant main layer. Selection export arrange horizontal subtract. Ellipse ipsum bullet rectangle flatten flatten inspect. Share share arrow invite italic draft plugin. Frame layout align figma rectangle mask. Flatten opacity create connection boolean rectangle overflow ellipse."
              textContent2="Figma ipsum component variant main layer. Selection export arrange horizontal subtract. Ellipse ipsum bullet rectangle flatten flatten inspect. Share share arrow invite italic draft plugin. Frame layout align figma rectangle mask. Flatten opacity create connection boolean rectangle overflow ellipse."
              textContent3="Figma ipsum component variant main layer. Selection export arrange horizontal subtract. Ellipse ipsum bullet rectangle flatten flatten inspect. Share share arrow invite italic draft plugin. Frame layout align figma rectangle mask. Flatten opacity create connection boolean rectangle overflow ellipse."
              onButtonClick={handleButtonClick}
              ongetStarted={handleNextClick}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

export default BecomeContentCreator;

const styleSheet = {
  contentCreatorBtn: {
    position: "absolute",
    top: { xs: -431, md: -222 },
    left: { xs: 234, md: 0 },
    width: { xs: "134px", md: "226px" },
    height: { xs: "39px", md: "62px" },
    fontSize: { xs: "13.64px", md: "18px" },
    fontWeight: 700,
    lineHeight: { xs: "16.37px", md: "21.6px" },
    p: { xs: "5px 0px 5px 5px", md: "8px 20px 8px 20px" },
    backgroundColor: "#233F91",
    textAlign: "start",
    borderRadius: { xs: "7.58px", md: "15px" },
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  completedBtn: {
    position: "absolute",
    top: { xs: -431, md: -222 },
    left: { xs: 234, md: 0 },
    width: { xs: "134px", md: "226px" },
    height: { xs: "39px", md: "62px" },
    fontSize: { xs: "13.64px", md: "18px" },
    fontWeight: 700,
    lineHeight: { xs: "16.37px", md: "21.6px" },
    p: { xs: "5px 0px 5px 5px", md: "8px 20px 8px 20px" },
    backgroundColor: "#F79E1B",
    textAlign: "start",
    borderRadius: { xs: "7.58px", md: "15px" },
    color: "white",
    "&:hover": {
      backgroundColor: "#FFB400",
      color: "white",
    },
  },
  saveChangesBtn: {
    display: { xs: "none", md: "block" },
    width: "214px",
    height: "48px",
    p: "8px, 20px, 8px, 20px",
    fontSize: "15px",
    fontWeight: 700,
    backgroundColor: "#233F91",
    borderRadius: "15px",
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  saveChangesBtnMobile: {
    display: { xs: "block", md: "none" },
    // margingTop: "20px",
    width: "214px",
    height: "48px",
    p: "8px, 20px, 8px, 20px",
    fontSize: "15px",
    fontWeight: 700,
    backgroundColor: "#233F91",
    borderRadius: "15px",
    color: "white",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "white",
    },
  },
  textButton: {
    color: "#000000",
    fontWeight: 400,
    fontSize: "17px",
  },
  buttonDeactive: {
    color: "#E10A0A",
    lineHeight: "24px",
    fontWeight: 700,
    fontSize: "15px",
  },
  nextButton: {
    color: "#595959",
    bgcolor: "#DBDBDB",
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 400,
    fontSize: "15px",
  },
  nextBtn7Con: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    py: 2,
    bgcolor: "#F8F8F8",
    display: "flex",
    justifyContent: "center",
  },
  confrimBtn: {
    position: "absolute",
    bottom: 0,
    width: "100%",
    py: 2,
    bgcolor: "#F8F8F8",
    display: "flex",
    justifyContent: "center",
  },
  nextBtn: {
    color: "#FFFFFF",
    bgcolor: "#595959",
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 700,
    fontSize: "18px",
    "&:hover": {
      backgroundColor: "#595959",
      color: "#FFFFFF",
    },
  },
  conformBtn: {
    color: "#EDEDED",
    bgcolor: "#233F91",
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 700,
    fontSize: "15px",
    "&:hover": {
      backgroundColor: "#233F91",
      color: "#FFFFFF",
    },
  },
  nextBtn2: {
    color: "#595959",
    bgcolor: "#DBDBDB",
    borderRadius: "10px",
    border: "1px solid #B3B3B3",
    fontWeight: 400,
    fontSize: "15px",
  },
  contentCreatorBorder: {
    borderLeft: { xs: "none", md: "9px solid #233F91" },
    height: "60px",
    position: "absolute",
    top: "-222px",
    left: "-15px",
  },

  contentCreatorBorder1: {
    borderLeft: { xs: "none", md: "9px solid #233F91" },
    height: "44px",
    position: "absolute",
    top: "-440px",
    left: "-15px",
  },
};
